/**
 * Secure Storage Backend
 *
 * Stores credentials in an encrypted file at ~/.origin-relay/credentials.enc
 * Uses AES-256-GCM for authenticated encryption.
 *
 * Encryption key is derived from OS-native hardware UUID using PBKDF2:
 * - macOS: IOPlatformUUID (tied to logic board, never changes)
 * - Windows: MachineGuid from registry (set at OS install)
 * - Linux: /var/lib/dbus/machine-id (set at OS install)
 *
 * This is more stable than the previous hostname-based derivation, which could
 * change with network/DHCP. Legacy credentials are auto-migrated on first load.
 *
 * Durability:
 * - Writes are atomic: data is written to `<file>.tmp` (mode 0o600) and then
 *   renamed into place. A crash mid-write cannot corrupt the live file.
 * - Unreadable files are NEVER deleted. If decryption fails (wrong
 *   `CRAFT_ENCRYPTION_KEY`, header mismatch, partial write, etc.) the file is
 *   renamed aside to `<file>.corrupt-<unix-timestamp>` for forensics and a
 *   loud warning is logged. This prevents a misconfigured deployment from
 *   wiping the only copy of the credentials.
 *
 * File format:
 *   [Header - 64 bytes]
 *   ├── Magic: "CRAFT01\0" (8 bytes)
 *   ├── Flags: uint32 LE (4 bytes) - reserved for future use
 *   ├── Salt: 32 bytes (PBKDF2 salt)
 *   ├── Reserved: 20 bytes
 *   [Encrypted Payload]
 *   ├── IV: 12 bytes (random per write)
 *   ├── Auth Tag: 16 bytes (GCM authentication)
 *   └── Ciphertext: variable (encrypted JSON)
 */

import {
  createCipheriv,
  createDecipheriv,
  randomBytes,
  pbkdf2Sync,
  createHash,
} from 'crypto';
import { execSync } from 'child_process';
import { existsSync, readFileSync, writeFileSync, mkdirSync, unlinkSync, renameSync } from 'fs';
import { hostname, userInfo, homedir } from 'os';
import { join, dirname } from 'path';

import type { CredentialBackend } from './types.ts';
import type { CredentialId, StoredCredential } from '../types.ts';
import { credentialIdToAccount, accountToCredentialId } from '../types.ts';
import { CONFIG_DIR } from '../../config/paths.ts';

// File location
const CREDENTIALS_DIR = CONFIG_DIR;
const CREDENTIALS_FILE = join(CREDENTIALS_DIR, 'credentials.enc');

// File format constants
const MAGIC_BYTES = Buffer.from('CRAFT01\0');
const HEADER_SIZE = 64;
const MAGIC_SIZE = 8;
const FLAGS_SIZE = 4;
const SALT_SIZE = 32;
const IV_SIZE = 12;
const AUTH_TAG_SIZE = 16;
const KEY_SIZE = 32;

// PBKDF2 iterations (balance security vs startup time)
const PBKDF2_ITERATIONS = 100000;

/**
 * Detect the host's stable machine identifier — an OS-native hardware UUID
 * (macOS IOPlatformUUID, Windows MachineGuid, Linux dbus machine-id). This
 * binds the encrypted credentials file to the host machine and is intentional
 * for desktop installs — losing the host means losing the credentials.
 *
 * Falls back to username + homedir if hardware UUID is unavailable.
 *
 * NOTE: this function does NOT consult `CRAFT_ENCRYPTION_KEY`. Callers that
 * want the env-var override (used by Docker / server deployments to make a
 * `credentials.enc` file portable, see `bun run encryption-key:export`)
 * must check the env var themselves and prefer it over this value.
 *
 * The legacy v1 derivation (see `getLegacyEncryptionKey`) remains hostname-
 * based to migrate pre-v2 host files; it does not call this function.
 */
export function detectStableMachineId(): string {
  try {
    if (process.platform === 'darwin') {
      // macOS: IOPlatformUUID - tied to logic board, never changes
      const output = execSync(
        'ioreg -rd1 -c IOPlatformExpertDevice | grep IOPlatformUUID',
        { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }
      );
      const match = output.match(/"IOPlatformUUID"\s*=\s*"([^"]+)"/);
      if (match?.[1]) return match[1];
    } else if (process.platform === 'win32') {
      // Windows: MachineGuid from registry - set at OS install
      const output = execSync(
        'reg query HKEY_LOCAL_MACHINE\\SOFTWARE\\Microsoft\\Cryptography /v MachineGuid',
        { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }
      );
      const match = output.match(/MachineGuid\s+REG_SZ\s+(\S+)/);
      if (match?.[1]) return match[1];
    } else {
      // Linux: dbus machine-id - set at OS install
      const machineIdPath = '/var/lib/dbus/machine-id';
      const altPath = '/etc/machine-id';
      if (existsSync(machineIdPath)) {
        return readFileSync(machineIdPath, 'utf-8').trim();
      } else if (existsSync(altPath)) {
        return readFileSync(altPath, 'utf-8').trim();
      }
    }
  } catch {
    // Fall through to fallback
  }

  // Fallback: username + homedir (stable enough for most cases)
  return `${userInfo().username}:${homedir()}`;
}

/** Internal credential store structure */
interface CredentialStore {
  version: 1;
  credentials: Record<string, StoredCredential>;
  metadata: {
    createdAt: number;
    updatedAt: number;
  };
}

export class SecureStorageBackend implements CredentialBackend {
  readonly name = 'secure-storage';
  readonly priority = 100;

  private cachedStore: CredentialStore | null = null;
  private encryptionKey: Buffer | null = null;
  private salt: Buffer | null = null;

  async isAvailable(): Promise<boolean> {
    // File backend is always available - we can always write to filesystem
    return true;
  }

  async get(id: CredentialId): Promise<StoredCredential | null> {
    const store = await this.loadStore();
    if (!store) return null;

    const key = credentialIdToAccount(id);
    return store.credentials[key] || null;
  }

  async set(id: CredentialId, credential: StoredCredential): Promise<void> {
    let store = await this.loadStore();

    if (!store) {
      // Initialize new store
      store = {
        version: 1,
        credentials: {},
        metadata: {
          createdAt: Date.now(),
          updatedAt: Date.now(),
        },
      };
    }

    const key = credentialIdToAccount(id);
    store.credentials[key] = credential;
    store.metadata.updatedAt = Date.now();

    await this.saveStore(store);
  }

  async delete(id: CredentialId): Promise<boolean> {
    const store = await this.loadStore();
    if (!store) return false;

    const key = credentialIdToAccount(id);
    if (!(key in store.credentials)) return false;

    delete store.credentials[key];
    store.metadata.updatedAt = Date.now();

    await this.saveStore(store);
    return true;
  }

  async list(filter?: Partial<CredentialId>): Promise<CredentialId[]> {
    const store = await this.loadStore();
    if (!store) return [];

    const ids = Object.keys(store.credentials)
      .map(accountToCredentialId)
      .filter((id): id is CredentialId => id !== null);

    if (!filter) return ids;

    return ids.filter((id) => {
      if (filter.type && id.type !== filter.type) return false;
      if (filter.workspaceId && id.workspaceId !== filter.workspaceId) return false;
      if (filter.name && id.name !== filter.name) return false;
      return true;
    });
  }

  // ============================================================
  // Private Methods
  // ============================================================

  private async loadStore(): Promise<CredentialStore | null> {
    // Return cached store if available
    if (this.cachedStore) return this.cachedStore;

    if (!existsSync(CREDENTIALS_FILE)) return null;

    let fileData: Buffer;
    try {
      fileData = readFileSync(CREDENTIALS_FILE);
    } catch {
      return null;
    }

    // Validate minimum size
    if (fileData.length < HEADER_SIZE + IV_SIZE + AUTH_TAG_SIZE) {
      // File is unreadable - preserve aside and return null
      this.handleUnreadableFile();
      return null;
    }

    // Validate magic bytes
    if (!fileData.subarray(0, MAGIC_SIZE).equals(MAGIC_BYTES)) {
      this.handleUnreadableFile();
      return null;
    }

    // Parse header
    // const flags = fileData.readUInt32LE(MAGIC_SIZE); // Reserved for future use
    const salt = fileData.subarray(MAGIC_SIZE + FLAGS_SIZE, MAGIC_SIZE + FLAGS_SIZE + SALT_SIZE);
    this.salt = salt;

    // Extract encrypted data
    const encryptedData = fileData.subarray(HEADER_SIZE);

    // Try new stable key first (v2 - hardware UUID based)
    const newKey = this.getEncryptionKey(salt);
    let store = this.tryDecrypt(encryptedData, newKey);

    if (store) {
      this.cachedStore = store;
      return store;
    }

    // Try legacy key for migration (v1 - included hostname)
    // This handles credentials encrypted with old key derivation
    const legacyKey = this.getLegacyEncryptionKey(salt);
    store = this.tryDecrypt(encryptedData, legacyKey);

    if (store) {
      // Migration: re-save with new stable key so future loads use hardware UUID
      this.cachedStore = store;
      await this.saveStore(store);
      return store;
    }

    // Both keys failed - file is unreadable
    this.handleUnreadableFile();
    return null;
  }

  /**
   * Attempt to decrypt data with given key.
   * Returns parsed store on success, null on failure.
   */
  private tryDecrypt(encryptedData: Buffer, key: Buffer): CredentialStore | null {
    try {
      const iv = encryptedData.subarray(0, IV_SIZE);
      const authTag = encryptedData.subarray(IV_SIZE, IV_SIZE + AUTH_TAG_SIZE);
      const ciphertext = encryptedData.subarray(IV_SIZE + AUTH_TAG_SIZE);

      const decipher = createDecipheriv('aes-256-gcm', key, iv);
      decipher.setAuthTag(authTag);
      const decrypted = Buffer.concat([decipher.update(ciphertext), decipher.final()]);
      return JSON.parse(decrypted.toString('utf8'));
    } catch {
      return null;
    }
  }

  private async saveStore(store: CredentialStore): Promise<void> {
    // Ensure directory exists
    if (!existsSync(CREDENTIALS_DIR)) {
      mkdirSync(CREDENTIALS_DIR, { recursive: true, mode: 0o700 });
    }

    // Use existing salt or generate new one
    const salt = this.salt || randomBytes(SALT_SIZE);
    this.salt = salt;

    // Get encryption key
    const key = this.getEncryptionKey(salt);

    // Serialize payload
    const plaintext = Buffer.from(JSON.stringify(store), 'utf8');

    // Generate new IV for each write (critical for GCM security)
    const iv = randomBytes(IV_SIZE);

    // Encrypt
    const cipher = createCipheriv('aes-256-gcm', key, iv);
    const ciphertext = Buffer.concat([cipher.update(plaintext), cipher.final()]);
    const authTag = cipher.getAuthTag();

    // Build header
    const header = Buffer.alloc(HEADER_SIZE);
    MAGIC_BYTES.copy(header, 0);
    header.writeUInt32LE(0, MAGIC_SIZE); // Flags (reserved)
    salt.copy(header, MAGIC_SIZE + FLAGS_SIZE);

    // Combine all parts
    const fileData = Buffer.concat([header, iv, authTag, ciphertext]);

    // Atomic write: write to <file>.tmp (mode 0o600) then rename into place.
    // A crash between writeFileSync and renameSync leaves the previous live
    // file untouched. If renameSync fails, attempt best-effort tmp cleanup
    // before re-throwing.
    const tmpPath = `${CREDENTIALS_FILE}.tmp`;
    writeFileSync(tmpPath, fileData, { mode: 0o600 });
    try {
      renameSync(tmpPath, CREDENTIALS_FILE);
    } catch (err) {
      try {
        unlinkSync(tmpPath);
      } catch {
        // best-effort cleanup; ignore
      }
      throw err;
    }
    this.cachedStore = store;
  }

  private getEncryptionKey(salt: Buffer): Buffer {
    if (this.encryptionKey) return this.encryptionKey;

    // New stable machine ID using hardware UUID (v2)
    // This is far more stable than hostname which can change with network/DHCP
    // Override: CRAFT_ENCRYPTION_KEY env var (used by Docker / server deployments
    // to make a credentials.enc file portable across hosts).
    const override = process.env.CRAFT_ENCRYPTION_KEY;
    const rawMachineId =
      override && override.length > 0 ? override : detectStableMachineId();
    const stableMachineId = createHash('sha256')
      .update(rawMachineId)
      .update('craft-agent-v2') // Bumped version for new key derivation
      .digest();

    // Derive key using PBKDF2
    this.encryptionKey = pbkdf2Sync(stableMachineId, salt, PBKDF2_ITERATIONS, KEY_SIZE, 'sha256');

    return this.encryptionKey;
  }

  /**
   * Legacy key derivation for migration from v1 (included hostname).
   * Used to decrypt credentials from older versions before re-encrypting with stable key.
   */
  private getLegacyEncryptionKey(salt: Buffer): Buffer {
    const legacyMachineId = createHash('sha256')
      .update(hostname())
      .update(userInfo().username)
      .update(homedir())
      .update('craft-agent-v1')
      .digest();

    return pbkdf2Sync(legacyMachineId, salt, PBKDF2_ITERATIONS, KEY_SIZE, 'sha256');
  }

  /**
   * Handle a credentials file we couldn't read or decrypt.
   *
   * IMPORTANT: we do NOT delete the file. A wrong CRAFT_ENCRYPTION_KEY, a
   * stale cached key, or a partial write would otherwise permanently erase
   * the only copy. Instead we rename it aside to
   * `<file>.corrupt-<unix-timestamp>` for forensics and emit a loud warning.
   * If the rename itself fails (permissions, disk full, etc.) we log and
   * leave the original file alone.
   */
  private handleUnreadableFile(): void {
    // NOTE: deliberately uses console.warn rather than the shared debug()
    // logger. This path emits CRITICAL credential-corruption warnings (a
    // bad CRAFT_ENCRYPTION_KEY just orphaned every saved credential).
    // The shared debug()/createLogger() helpers in @craft-agent/shared/utils
    // gate on isDebugEnabled() — without --debug they emit nothing, which
    // would silently swallow these warnings on the boot path before the
    // host (Electron / headless / Slack) has had a chance to wire its own
    // logger. Stay on console.warn so this always reaches stderr.
    try {
      if (existsSync(CREDENTIALS_FILE)) {
        const asidePath = `${CREDENTIALS_FILE}.corrupt-${Math.floor(Date.now() / 1000)}`;
        try {
          renameSync(CREDENTIALS_FILE, asidePath);
          console.warn(
            `[credentials] Failed to decrypt ${CREDENTIALS_FILE} — file moved aside to ${asidePath}. ` +
              `Likely cause: wrong CRAFT_ENCRYPTION_KEY env var or corrupted file.`
          );
        } catch (err) {
          console.warn(
            `[credentials] Failed to decrypt ${CREDENTIALS_FILE} and could not move it aside (${
              (err as Error).message
            }); leaving the file in place. ` +
              `Likely cause: wrong CRAFT_ENCRYPTION_KEY env var or corrupted file.`
          );
        }
      }
    } catch {
      // Defensive: never throw from this path.
    }
    this.cachedStore = null;
    this.encryptionKey = null;
    this.salt = null;
  }

  /** Clear cached data (for testing or forced refresh) */
  clearCache(): void {
    this.cachedStore = null;
    this.encryptionKey = null;
    this.salt = null;
  }
}
